import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, UserPlus, Send, Link2, Trash2, Copy, Check, Clock, CheckCircle2, XCircle } from 'lucide-react';

const API = window.location.origin + '/api';

const STATUS_META = {
  pending: { label: 'Pending', bg: '#DBEAFE', color: '#1E3A8A', icon: Clock },
  used:    { label: 'Used',    bg: '#D1FAE5', color: '#065F46', icon: CheckCircle2 },
  expired: { label: 'Expired', bg: '#FEE2E2', color: '#991B1B', icon: XCircle },
};

function formatDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
}

export default function AdminInvites() {
  const [invites, setInvites] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ supermarket_id: '', email: '', expires_days: 14 });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [copied, setCopied] = useState('');
  const navigate = useNavigate();

  const load = () => {
    axios.get(`${API}/admin/store-admin-invites`, { withCredentials: true })
      .then(r => setInvites(r.data.invites || []))
      .catch(err => console.error('Failed to load invites:', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    axios.get(`${API}/admin/supermarkets`, { withCredentials: true })
      .then(r => {
        setStores(r.data || []);
        if (r.data?.[0]) setForm(f => ({ ...f, supermarket_id: r.data[0].id }));
      });
  }, []);

  const inviteUrl = (token) => `${window.location.origin}/invite/${token}`;

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(''), 1500);
    } catch (e) { /* ignore */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    setCreateError('');
    if (!form.supermarket_id) { setCreateError('Pick a supermarket'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/store-admin-invites`, {
        supermarket_id: form.supermarket_id,
        email: (form.email || '').trim(),
        expires_days: Number(form.expires_days) || 14,
      }, { withCredentials: true });
      setForm({ ...form, email: '' });
      load();
    } catch (err) {
      setCreateError(err.response?.data?.detail || 'Could not create invite');
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (inv) => {
    if (!window.confirm(`Revoke invite for ${inv.supermarket_name}?`)) return;
    try {
      await axios.delete(`${API}/admin/store-admin-invites/${inv.id}`, { withCredentials: true });
      setInvites(prev => prev.filter(x => x.id !== inv.id));
    } catch (err) {
      alert('Could not revoke invite');
    }
  };

  const shareOnWhatsApp = (inv) => {
    const text = encodeURIComponent(
      `Hi 👋 Here's your invite to become a StoreRuns admin for ${inv.supermarket_name}. ` +
      `Click to set up your account (link expires ${formatDate(inv.expires_at)}):\n\n${inviteUrl(inv.token)}`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  return (
    <div className="admin-page" data-testid="admin-invites-page" style={{ maxWidth: 1050, margin: '0 auto' }}>
      <button className="back-btn" onClick={() => navigate('/admin')}>
        <ArrowLeft /> Admin
      </button>
      <h2 style={{ margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <UserPlus size={22} /> Store Admin Invites
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '0 0 20px' }}>
        Send a one-time link to a supermarket owner. They set their own password and land straight in their scoped dashboard.
      </p>

      {/* Create form */}
      <form onSubmit={submit} style={{ background: 'white', border: '1px solid var(--border-gray)', borderRadius: 12, padding: 18, marginBottom: 20 }} data-testid="new-invite-form">
        <h3 style={{ margin: '0 0 12px', fontSize: '0.95rem', color: 'var(--navy)' }}>Send new invite</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
          <label style={fieldWrap}>
            <span style={fieldLabel}>Supermarket*</span>
            <select value={form.supermarket_id} onChange={e => setForm({ ...form, supermarket_id: e.target.value })}
              data-testid="inv-store" style={inputStyle} required>
              {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <label style={fieldWrap}>
            <span style={fieldLabel}>Owner email (optional)</span>
            <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
              placeholder="Lock invite to a specific address" data-testid="inv-email" style={inputStyle} />
          </label>
          <label style={fieldWrap}>
            <span style={fieldLabel}>Expires in (days)</span>
            <input type="number" min="1" max="60" value={form.expires_days}
              onChange={e => setForm({ ...form, expires_days: e.target.value })}
              data-testid="inv-expires" style={inputStyle} />
          </label>
        </div>
        {createError && <div style={{ marginTop: 10, padding: 8, background: '#FEF2F2', color: '#991B1B', borderRadius: 6, fontSize: '0.82rem' }}>{createError}</div>}
        <button type="submit" disabled={creating} data-testid="inv-submit"
          style={{ marginTop: 12, padding: '9px 16px', background: 'var(--navy)', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, opacity: creating ? 0.7 : 1 }}>
          <Send size={14} /> {creating ? 'Creating…' : 'Generate invite link'}
        </button>
      </form>

      {loading && <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>}
      {!loading && invites.length === 0 && (
        <div data-testid="invites-empty" style={{ padding: 40, textAlign: 'center', background: 'white', borderRadius: 12, border: '1px dashed var(--border-gray)', color: 'var(--text-muted)' }}>
          <UserPlus size={26} style={{ marginBottom: 8, opacity: 0.4 }} />
          <p style={{ margin: 0 }}>No invites yet. Send your first one above.</p>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {invites.map(inv => {
          const meta = STATUS_META[inv.status] || STATUS_META.pending;
          const Icon = meta.icon;
          const url = inviteUrl(inv.token);
          return (
            <div key={inv.id} data-testid={`invite-card-${inv.id}`}
              style={{ background: 'white', border: '1px solid var(--border-gray)',
                borderLeft: `4px solid ${meta.color}`, borderRadius: 12, padding: 16,
                opacity: inv.status === 'used' || inv.status === 'expired' ? 0.75 : 1 }}>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <strong style={{ color: 'var(--navy)', fontSize: '1rem' }}>{inv.supermarket_name}</strong>
                    <span style={{ background: meta.bg, color: meta.color, padding: '2px 10px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.05, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Icon size={11} /> {meta.label}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 3 }}>
                    {inv.email && <>Locked to: <strong>{inv.email}</strong> · </>}
                    Sent {formatDate(inv.created_at)} · Expires {formatDate(inv.expires_at)}
                  </div>
                </div>
              </div>

              {inv.status === 'pending' && (
                <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <code style={{ flex: 1, minWidth: 200, background: '#f8fafc', padding: '7px 12px', borderRadius: 6, fontSize: '0.75rem', color: '#334155', border: '1px solid var(--border-gray)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    data-testid={`invite-url-${inv.id}`}>
                    {url}
                  </code>
                  <button onClick={() => copy(url, inv.id)} data-testid={`invite-copy-${inv.id}`}
                    style={{ padding: '7px 12px', background: 'var(--navy)', color: 'white', border: 'none', borderRadius: 6, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    {copied === inv.id ? <Check size={13} /> : <Copy size={13} />}
                    {copied === inv.id ? 'Copied' : 'Copy link'}
                  </button>
                  <button onClick={() => shareOnWhatsApp(inv)} data-testid={`invite-whatsapp-${inv.id}`}
                    style={{ padding: '7px 12px', background: '#25D366', color: 'white', border: 'none', borderRadius: 6, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <Link2 size={13} /> WhatsApp
                  </button>
                  <button onClick={() => revoke(inv)} data-testid={`invite-revoke-${inv.id}`}
                    style={{ padding: '7px 12px', background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <Trash2 size={13} /> Revoke
                  </button>
                </div>
              )}
              {inv.status !== 'pending' && (
                <div style={{ marginTop: 8 }}>
                  <button onClick={() => revoke(inv)} data-testid={`invite-revoke-${inv.id}`}
                    style={{ padding: '5px 10px', background: 'transparent', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <Trash2 size={12} /> Delete record
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const inputStyle = { padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border-gray)', fontSize: '0.88rem', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' };
const fieldWrap = { display: 'flex', flexDirection: 'column', gap: 4 };
const fieldLabel = { fontSize: '0.78rem', fontWeight: 600, color: '#475569' };
